import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { withdrawalAPI, apiUtils } from '../api';
import Nav from './nav';
import { useNavigate } from 'react-router-dom';

const WithdrawPage = () => {
  const [step, setStep] = useState(1);
  const [selectedCoin, setSelectedCoin] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [cryptoAddress, setCryptoAddress] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('crypto'); // 'crypto' or 'bank'
  const navigate = useNavigate();

  const coins = [
    { symbol: 'BTC', name: 'Bitcoin', image: '/images/coins/btc.jpg', fee: 0.0005, minWithdraw: 0.001 },
    { symbol: 'ETH', name: 'Ethereum', image: '/images/coins/eth.jpg', fee: 0.005, minWithdraw: 0.01 },
    { symbol: 'USDT', name: 'Tether', image: '/images/coins/usdt.png', fee: 1, minWithdraw: 10 },
    { symbol: 'BDX', name: 'Beldex', image: '/images/coins/bdx.png', fee: 0.1, minWithdraw: 10 },
    { symbol: 'TRX', name: 'Tron', image: '/images/coins/trx.png', fee: 1, minWithdraw: 10 },
    { symbol: 'XRP', name: 'Ripple', image: '/images/coins/xrp.png', fee: 0.1, minWithdraw: 10 },
    { symbol: 'DOGE', name: 'Dogecoin', image: '/images/coins/doge.png', fee: 1, minWithdraw: 10 },
    { symbol: 'BNB', name: 'Binance Coin', image: '/images/coins/bnb.jpg', fee: 0.001, minWithdraw: 0.1 }
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userEmail = localStorage.getItem('userEmail');
    
    if (!token || !userEmail) {
      navigate('/login');
      return;
    }

    fetchWithdrawHistory();
    fetchBalance();
    fetchSavedAccounts();
  }, [navigate]);

  const fetchWithdrawHistory = async () => {
    try {
      setLoading(true);
      const response = await withdrawalAPI.getWithdrawals();
      setWithdrawHistory(response.data);
    } catch (error) {
      console.error('Error fetching withdraw history:', error);
      const errorInfo = apiUtils.handleAPIError(error);
      setError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await axios.get('/api/user/balance');
      setAvailableBalance(response.data.balance || 0);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchSavedAccounts = async () => {
    try {
      // Mock saved accounts - replace with actual API call
      const mockAccounts = [
        { id: 1, bankName: 'Chase Bank', accountNumber: '****1234', accountName: 'John Doe' },
        { id: 2, bankName: 'Bank of America', accountNumber: '****5678', accountName: 'John Doe' }
      ];
      setSavedAccounts(mockAccounts);
    } catch (error) {
      console.error('Error fetching saved accounts:', error);
    }
  };

  const handleCoinSelect = (coin) => {
    setSelectedCoin(coin);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
  };

  const handleAccountSelect = (account) => {
    setBankName(account.bankName);
    setAccountNumber(account.accountNumber.replace('****', ''));
    setAccountName(account.accountName);
    setShowAccountForm(false);
  };

  const validateForm = () => {
    if (!amount || amount.trim() === '') {
      setError('Please enter a valid amount');
      return false;
    }

    // For crypto, check if amount is a valid number
    if (paymentMethod === 'crypto') {
      const amountNum = parseFloat(amount);
      if (isNaN(amountNum) || amountNum <= 0) {
        setError('Please enter a valid amount');
        return false;
      }

      const selectedCoinData = coins.find(coin => coin.symbol === selectedCoin);
      if (amountNum < selectedCoinData.minWithdraw) {
        setError(`Minimum withdrawal amount for ${selectedCoin} is ${selectedCoinData.minWithdraw}`);
        return false;
      }

      if (!cryptoAddress || cryptoAddress.trim() === '') {
        setError('Please enter a valid cryptocurrency address');
        return false;
      }

      // Basic crypto address validation (can be enhanced for specific coins)
      if (cryptoAddress.length < 10) {
        setError('Please enter a valid cryptocurrency address');
        return false;
      }
    }

    if (paymentMethod === 'bank' && (!accountNumber || !bankName || !accountName)) {
      setError('Please fill in all bank account details');
      return false;
    }

    if (paymentMethod === 'bank' && accountNumber.length < 4) {
      setError('Please enter a valid account number');
      return false;
    }

    return true;
  };

  const calculateFee = () => {
    if (paymentMethod === 'bank') {
      return 0; // No fee for bank transfers, or set a fixed fee
    }
    const coin = coins.find(c => c.symbol === selectedCoin);
    return coin ? coin.fee : 0;
  };

  const calculateTotal = () => {
    if (paymentMethod === 'crypto') {
      const numAmount = parseFloat(amount) || 0;
      const fee = calculateFee();
      return (numAmount + fee).toFixed(8);
    }
    return amount; // For bank, return the string amount as is
  };

  const handleWithdraw = async () => {
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      let withdrawData;
      
      if (paymentMethod === 'bank') {
        // For bank transfers, send bank details
        const bankDetails = `${bankName}|${accountNumber}|${accountName}`;
        withdrawData = {
          amount: amount, // Keep as string for bank
          currency: 'USD', // Or your fiat currency
          bankDetails: bankDetails,
          fee: '0', // No fee for bank
          totalAmount: amount,
          paymentMethod: 'bank'
        };
      } else {
        // For crypto withdrawals
        const amountNum = parseFloat(amount);
        withdrawData = {
          amount: amountNum,
          currency: selectedCoin,
          cryptoAddress: cryptoAddress,
          fee: calculateFee(),
          totalAmount: calculateTotal(),
          paymentMethod: 'crypto'
        };
      }

      await withdrawalAPI.createWithdrawal(withdrawData);
      
      setSuccess('Withdrawal request submitted successfully!');
      await fetchWithdrawHistory();
      await fetchBalance();
      setStep(2);
      
    } catch (error) {
      console.error('Withdrawal failed:', error);
      const errorInfo = apiUtils.handleAPIError(error);
      setError(errorInfo.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const quickAmounts = [50, 100, 200, 500, 1000, 2000];
  const selectedCoinData = coins.find(coin => coin.symbol === selectedCoin);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'failed': return 'text-red-400';
      case 'processing': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'fas fa-check-circle';
      case 'pending': return 'fas fa-clock';
      case 'failed': return 'fas fa-times-circle';
      case 'processing': return 'fas fa-sync-alt animate-spin';
      default: return 'fas fa-question-circle';
    }
  };

  const maskAccountNumber = (number) => {
    return `****${number.slice(-4)}`;
  };

  const maskCryptoAddress = (address) => {
    if (address.length <= 16) return address;
    return `${address.slice(0, 8)}...${address.slice(-8)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white pb-20">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-lg border-b border-gray-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/user/wallet')}
              className="p-2 hover:bg-gray-700/50 rounded-xl transition-all duration-200"
            >
              <i className="fas fa-arrow-left text-lg"></i>
            </button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              {step === 1 && 'Withdraw Funds'}
              {step === 2 && 'Withdrawal Requested'}
            </h1>
            <button 
              onClick={() => setShowHistory(true)}
              className="p-2 hover:bg-gray-700/50 rounded-xl transition-all duration-200"
            >
              <i className="fas fa-history text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-lg">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 backdrop-blur-sm">
            <div className="flex items-center gap-3 text-red-400">
              <i className="fas fa-exclamation-circle text-lg"></i>
              <span className="text-sm font-medium">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 mb-6 backdrop-blur-sm">
            <div className="flex items-center gap-3 text-green-400">
              <i className="fas fa-check-circle text-lg"></i>
              <span className="text-sm font-medium">{success}</span>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            {/* Balance Card */}
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-gray-700/50 rounded-2xl p-6 text-center backdrop-blur-sm">
              {/* <div className="text-blue-200 text-sm mb-1">Available Balance</div>
              <div className="text-3xl font-bold mb-1 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                ${availableBalance.toLocaleString()}
              </div> */}
              <div className="text-blue-200 text-sm">Ready to withdraw</div>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <h2 className="text-lg font-semibold mb-4">Payment Method</h2>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('crypto')}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 ${
                    paymentMethod === 'crypto'
                      ? 'border-blue-500 bg-blue-500/20 shadow-lg shadow-blue-500/25'
                      : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                  }`}
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-2">
                    <i className="fas fa-coins text-white text-lg"></i>
                  </div>
                  <span className="font-medium">Cryptocurrency</span>
                  <span className="text-xs text-gray-400 mt-1">Instant</span>
                </button>
                
                <button
                  onClick={() => setPaymentMethod('bank')}
                  className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200 ${
                    paymentMethod === 'bank'
                      ? 'border-blue-500 bg-blue-500/20 shadow-lg shadow-blue-500/25'
                      : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                  }`}
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mb-2">
                    <i className="fas fa-university text-white text-lg"></i>
                  </div>
                  <span className="font-medium">Bank Transfer</span>
                  <span className="text-xs text-gray-400 mt-1">1-3 days</span>
                </button>
              </div>
            </div>

            {/* Crypto Selection - Only show if payment method is crypto */}
            {paymentMethod === 'crypto' && (
              <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
                <h2 className="text-lg font-semibold mb-4">Select Currency</h2>
                <div className="grid grid-cols-4 gap-3">
                  {coins.map((coin) => (
                    <button
                      key={coin.symbol}
                      onClick={() => handleCoinSelect(coin.symbol)}
                      className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-200 ${
                        selectedCoin === coin.symbol
                          ? 'border-blue-500 bg-blue-500/20 shadow-lg shadow-blue-500/25'
                          : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                      }`}
                    >
                      <img 
                        src={coin.image} 
                        alt={coin.name}
                        className="w-10 h-10 rounded-full mb-2"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iMjAiIGZpbGw9IiMzNzM3NDAiLz4KPC9zdmc+';
                        }}
                      />
                      <span className="text-xs font-medium">{coin.symbol}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex justify-between items-center mb-4">
                <label className="text-lg font-semibold">
                  Amount {paymentMethod === 'crypto' ? `(${selectedCoin})` : ''}
                </label>
                {paymentMethod === 'crypto' && (
                  <button 
                    onClick={() => setAmount(availableBalance.toString())}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                  >
                    Max
                  </button>
                )}
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl py-4 px-4 text-2xl font-bold focus:outline-none focus:border-blue-500 transition-colors backdrop-blur-sm"
                  placeholder={paymentMethod === 'crypto' ? "0.00" : "Enter amount"}
                />
                {amount && (
                  <button 
                    onClick={() => setAmount('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>

              {paymentMethod === 'crypto' && (
                <div className="mt-4">
                  <div className="text-sm text-gray-400 mb-2">Quick Select</div>
                  <div className="grid grid-cols-3 gap-2">
                    {quickAmounts.map((quickAmount) => (
                      <button
                        key={quickAmount}
                        onClick={() => setAmount(quickAmount.toString())}
                        className="py-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-all duration-200 text-sm font-medium backdrop-blur-sm"
                      >
                        ${quickAmount}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Crypto Address Input - Only show if payment method is crypto */}
            {paymentMethod === 'crypto' && (
              <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
                <h2 className="text-lg font-semibold mb-4">Recipient Address</h2>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Cryptocurrency Address</label>
                    <input
                      type="text"
                      value={cryptoAddress}
                      onChange={(e) => setCryptoAddress(e.target.value)}
                      className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl py-3 px-4 focus:outline-none focus:border-blue-500 transition-colors font-mono backdrop-blur-sm"
                      placeholder={`Enter your ${selectedCoin} address`}
                    />
                  </div>
                  <div className="text-xs text-gray-400">
                    <i className="fas fa-info-circle mr-1"></i>
                    Please double-check the address before confirming
                  </div>
                </div>
              </div>
            )}

            {/* Bank Account Form - Only show if payment method is bank */}
            {paymentMethod === 'bank' && (
              <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex justify-between items-center mb-4">
                  <label className="text-lg font-semibold">Bank Account</label>
                  <button 
                    onClick={() => setShowAccountForm(!showAccountForm)}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                  >
                    {showAccountForm ? 'Cancel' : 'New Account'}
                  </button>
                </div>

                {/* Saved Accounts */}
                {!showAccountForm && savedAccounts.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {savedAccounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => handleAccountSelect(account)}
                        className="w-full flex items-center justify-between p-4 bg-gray-700/30 hover:bg-gray-700/50 rounded-xl transition-all duration-200 backdrop-blur-sm"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-600 rounded-xl flex items-center justify-center">
                            <i className="fas fa-university text-white text-sm"></i>
                          </div>
                          <div className="text-left">
                            <div className="font-semibold">{account.bankName}</div>
                            <div className="text-gray-400 text-sm">{account.accountNumber}</div>
                          </div>
                        </div>
                        <i className="fas fa-chevron-right text-gray-400"></i>
                      </button>
                    ))}
                  </div>
                )}

                {/* Bank Account Form */}
                {(showAccountForm || savedAccounts.length === 0) && (
                  <div className="space-y-4 animate-fade-in">
                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Bank Name</label>
                      <input
                        type="text"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value)}
                        className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl py-3 px-4 focus:outline-none focus:border-blue-500 transition-colors backdrop-blur-sm"
                        placeholder="Enter your bank name"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Account Number</label>
                      <input
                        type="text"
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value)}
                        className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl py-3 px-4 focus:outline-none focus:border-blue-500 transition-colors font-mono backdrop-blur-sm"
                        placeholder="Enter account number"
                        maxLength={20}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Account Holder Name</label>
                      <input
                        type="text"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl py-3 px-4 focus:outline-none focus:border-blue-500 transition-colors backdrop-blur-sm"
                        placeholder="Enter account holder name"
                      />
                    </div>
                  </div>
                )}

                {/* Selected Account Preview */}
                {bankName && accountNumber && !showAccountForm && (
                  <div className="bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/20 rounded-xl p-4 mt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">{bankName}</div>
                        <div className="text-gray-400 text-sm">
                          {maskAccountNumber(accountNumber)} • {accountName}
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowAccountForm(true)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Fee Information */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-4">Withdrawal Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Withdrawal Amount</span>
                  <span className="font-semibold">
                    {amount || '0'} {paymentMethod === 'crypto' ? selectedCoin : ''}
                  </span>
                </div>
                {paymentMethod === 'crypto' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Network Fee</span>
                      <span className="font-semibold">
                        {calculateFee()} {selectedCoin}
                      </span>
                    </div>
                    <div className="border-t border-gray-600 pt-3 flex justify-between">
                      <span className="text-gray-400">Total Deducted</span>
                      <span className="font-bold text-lg text-white">
                        {calculateTotal()} {selectedCoin}
                      </span>
                    </div>
                  </>
                )}
                {paymentMethod === 'crypto' && selectedCoinData && (
                  <div className="text-xs text-gray-400 text-center">
                    Minimum withdrawal: {selectedCoinData.minWithdraw} {selectedCoin}
                  </div>
                )}
              </div>
            </div>

            {/* Processing Time */}
            <div className={`${
              paymentMethod === 'crypto' ? 'bg-blue-500/10 border-blue-500/20' : 'bg-green-500/10 border-green-500/20'
            } border rounded-2xl p-4`}>
              <div className="flex items-start gap-3">
                <i className={`fas ${
                  paymentMethod === 'crypto' ? 'fa-bolt text-blue-400' : 'fa-clock text-green-400'
                } mt-1`}></i>
                <div className={`text-sm ${
                  paymentMethod === 'crypto' ? 'text-blue-300' : 'text-green-300'
                }`}>
                  <p className="font-semibold">
                    {paymentMethod === 'crypto' ? 'Instant Processing' : 'Processing Time'}
                  </p>
                  <p className="mt-1">
                    {paymentMethod === 'crypto' 
                      ? 'Cryptocurrency withdrawals are typically processed within minutes.' 
                      : 'Bank transfers typically take 1-3 business days to process.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Withdraw Button */}
            <button
              onClick={handleWithdraw}
              disabled={
                !amount || 
                amount.trim() === '' ||
                (paymentMethod === 'crypto' && (!cryptoAddress || cryptoAddress.trim() === '')) ||
                (paymentMethod === 'bank' && (!accountNumber || !bankName || !accountName)) ||
                isProcessing
              }
              className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-red-500/25"
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </div>
              ) : (
                `Confirm ${paymentMethod === 'crypto' ? 'Crypto' : 'Bank'} Withdrawal`
              )}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="text-center space-y-6">
            {/* Success Card */}
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8 backdrop-blur-sm">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/25">
                <i className="fas fa-check text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Withdrawal Requested!
              </h2>
              <p className="text-gray-400">
                Your {paymentMethod === 'crypto' ? 'crypto' : 'bank'} withdrawal of{' '}
                <span className="text-white font-semibold">
                  {amount} {paymentMethod === 'crypto' ? selectedCoin : ''}
                </span>{' '}
                is being processed.
              </p>
            </div>

            {/* Transaction Details */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-4 text-left">Transaction Details</h3>
              <div className="space-y-4 text-sm text-left">
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount</span>
                  <span className="font-semibold">
                    {amount} {paymentMethod === 'crypto' ? selectedCoin : ''}
                  </span>
                </div>
                {paymentMethod === 'crypto' && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Network Fee</span>
                    <span className="font-semibold">
                      {calculateFee()} {selectedCoin}
                    </span>
                  </div>
                )}
                {paymentMethod === 'crypto' ? (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Recipient Address</span>
                    <span className="font-semibold text-right font-mono text-xs">
                      {maskCryptoAddress(cryptoAddress)}
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Bank Account</span>
                    <span className="font-semibold text-right">{bankName} ({maskAccountNumber(accountNumber)})</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="text-yellow-400 font-semibold flex items-center gap-2">
                    <i className="fas fa-clock"></i>
                    Processing
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Estimated Time</span>
                  <span className="font-semibold">
                    {paymentMethod === 'crypto' ? 'Few minutes' : '1-3 business days'}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setStep(1);
                  setAmount('');
                  setAccountNumber('');
                  setBankName('');
                  setAccountName('');
                  setCryptoAddress('');
                }}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25"
              >
                New Withdrawal
              </button>
              <button
                onClick={() => navigate('/user/wallet')}
                className="w-full bg-gray-700/50 hover:bg-gray-700 border border-gray-600 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 backdrop-blur-sm"
              >
                View Dashboard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end justify-center z-50 px-4 pb-4 sm:items-center sm:p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Withdrawal History</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-gray-700/50 rounded-xl transition-all duration-200"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : withdrawHistory.length > 0 ? (
                <div className="space-y-3">
                  {withdrawHistory.map((withdraw) => (
                    <div key={withdraw._id} className="flex items-center justify-between p-4 bg-gray-700/30 rounded-xl backdrop-blur-sm">
                      <div className="flex items-center space-x-3 flex-1">
                        <i className={`${getStatusIcon(withdraw.status)} ${getStatusColor(withdraw.status)} text-lg`}></i>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{withdraw.amount} {withdraw.currency}</p>
                          <p className="text-xs text-gray-400 truncate">{formatDate(withdraw.createdAt)}</p>
                        </div>
                      </div>
                      <span className={`text-sm font-medium ${getStatusColor(withdraw.status)}`}>
                        {withdraw.status.charAt(0).toUpperCase() + withdraw.status.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <i className="fas fa-history text-3xl mb-3"></i>
                  <p>No withdrawal history found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <Nav />

      {/* Include Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      
      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default WithdrawPage;